/**
 * ProtocolsButton — SlashSquare trigger for the shortcuts "/" menu.
 */

import { useIsAskHeidiSourcesShortcutsEnabled } from '@/features/flags';
import { IS_PROTOCOLS_ENABLED } from '@/features/flags/legacy-flags';
import { useQueryKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { useProtocolsList } from '@/hooks/query/useQueryProtocols';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';
import { useTranslations } from '@heidi/runtime/i18n';
import { Region } from '@heidi/schema/ml-scribe';
import { forwardRef, memo, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { type PinnedChip } from './evidence-settings-popover';
import type {
  ProtocolsButtonHandle,
  ProtocolsButtonProps,
} from './protocols-button.types';
import { ProtocolsDesktopDropdown } from './protocols-desktop-dropdown';
import { ProtocolsMobileDrawer } from './protocols-mobile-drawer';
import { DisabledProtocolsTrigger, ProtocolsTrigger } from './protocols-trigger-button';
import { getResolvedShortcutsForMarket } from './shortcut-marketplace-data';
import { ShortcutMarketplaceModal } from './shortcut-marketplace-modal';
import { useProtocolsActions } from './use-protocols-actions';
import { useRecommendedShortcuts } from './use-recommended-shortcuts';
import { useShortcutCommandSearch } from './use-shortcut-command-search';
import { useShortcutMarketplacePins } from './use-shortcut-marketplace-pins';

export type { ProtocolsButtonProps } from './protocols-button.types';

export const ProtocolsButton = memo(
  forwardRef<ProtocolsButtonHandle, ProtocolsButtonProps>(
    (
      {
        pinnedChips,
        onPinnedChipClick,
        disabled = false,
        disabledTooltip,
        open,
        onOpenChange,
        onAddProtocol,
        onEditProtocol,
        onRemoveProtocol,
        onRestoreDefaultShortcut,
        isAtShortcutLimit,
        shortcutCount,
        maxShortcuts,
        hideSystemProtocols = false,
        isPreNoteSession = false,
        searchQuery,
        onRetainInputFocus,
      }: ProtocolsButtonProps,
      ref
    ) => {
      const { t } = useTranslations();
      const intl = useIntl();
      const isMobile = useIsMobile();
      // Support both controlled (open/onOpenChange) and uncontrolled modes
      const [internalOpen, setInternalOpen] = useState(false);
      const [showMarketplace, setShowMarketplace] = useState(false);
      const isControlled = open !== undefined;
      const isOpen = isControlled ? open : internalOpen;

      const { data: kindeProperties } = useQueryKindeProperties();
      const isUS = kindeProperties?.region === Region.US;
      const marketShortcuts = useMemo(
        () => getResolvedShortcutsForMarket(isUS, intl),
        [intl, isUS]
      );

      // Fetch curated (system) protocols when the feature is enabled.
      // Hidden in sessionless contexts via hideSystemProtocols since they
      // require capabilities like patient_summary.
      const protocolsEnabled = IS_PROTOCOLS_ENABLED();
      const { systemProtocols } = useProtocolsList();
      const activeCuratedProtocols = useMemo(
        () =>
          protocolsEnabled && !hideSystemProtocols
            ? systemProtocols.filter((p) => p.is_active)
            : [],
        [protocolsEnabled, hideSystemProtocols, systemProtocols]
      );
      // Show custom tooltip when disabled, otherwise show default "Shortcuts"
      const tooltipText =
        disabled && disabledTooltip
          ? disabledTooltip
          : t('floatingAskHeidiShortcuts.shortcuts');

      const { pinnedShortcutIds, pinnedShortcutLabels, customShortcuts } =
        useShortcutMarketplacePins(pinnedChips);

      // Handle dropdown open state changes
      const handleOpenChange = useCallback(
        (newOpen: boolean) => {
          if (!isControlled) {
            setInternalOpen(newOpen);
          }
          onOpenChange?.(newOpen);

          if (newOpen) {
            track({
              event_name: TrackingEventName.EvidenceShortcutsOpened,
              props: {
                shortcut_count: pinnedChips.length,
                is_pre_note_session: isPreNoteSession,
                open_method: 'trigger',
              },
              options: { amplitude: false },
            });
          }
        },
        [isControlled, onOpenChange, pinnedChips.length, isPreNoteSession]
      );

      // Guided menu (recommendations + "/" search + inline save) is kill-switched
      // by release-ask-heidi-sources-shortcuts. Old shortcuts cohort keeps the
      // pinned + curated browse menu without the redesign.
      const isGuidedMenuEnabled = useIsAskHeidiSourcesShortcutsEnabled();

      // Shared add/remove/select handlers (menu, "/" search, marketplace modal).
      const { handleMarketplaceAdd, handleMarketplaceRemove, handleProtocolSelect } =
        useProtocolsActions({
          pinnedChips,
          marketShortcuts,
          onAddProtocol,
          onRestoreDefaultShortcut,
          onRemoveProtocol,
          onPinnedChipClick,
        });

      const { recommendedShortcuts } = useRecommendedShortcuts({
        pinnedChips,
        marketShortcuts,
        isPreNoteSession,
      });

      const onRecommendedSelect = useCallback(
        (shortcut: (typeof marketShortcuts)[number]) =>
          onPinnedChipClick?.({
            id: shortcut.id,
            label: shortcut.label,
            query: shortcut.query,
            category: shortcut.category,
          }),
        [onPinnedChipClick]
      );

      const onSaveShortcut = useCallback(
        (shortcut: (typeof marketShortcuts)[number]) =>
          handleMarketplaceAdd(shortcut, 'library'),
        [handleMarketplaceAdd]
      );

      const onUnsaveShortcut = useMemo(
        () =>
          onRemoveProtocol
            ? (chip: PinnedChip) => {
                track({
                  event_name: TrackingEventName.EvidenceShortcutSaveToggled,
                  props: { action: 'unsaved', shortcut_id: chip.id },
                  options: { amplitude: false },
                });
                onRemoveProtocol(chip.id);
              }
            : undefined,
        [onRemoveProtocol]
      );

      const compactMenu = isGuidedMenuEnabled
        ? {
            recommendedShortcuts,
            onRecommendedSelect,
            onSaveShortcut,
            onUnsaveShortcut,
          }
        : {};

      const search = useShortcutCommandSearch({
        handleRef: ref,
        pinnedChips,
        marketShortcuts,
        searchQuery: isGuidedMenuEnabled ? searchQuery : undefined,
        onRun: onRecommendedSelect,
        onSave: onSaveShortcut,
        onUnsave: onRemoveProtocol ? handleMarketplaceRemove : undefined,
      });

      // Handle browse all click
      const handleBrowseAllClick = useCallback(() => {
        handleOpenChange(false);
        setShowMarketplace(true);

        track({
          event_name: TrackingEventName.ButtonClicked,
          props: {
            action: ButtonAction.AskHeidiShortcutsBrowseAll,
            label: 'shortcuts_browse_all',
            shortcut_count: pinnedChips.length,
          },
          options: { amplitude: false },
        });
      }, [handleOpenChange, pinnedChips.length]);

      const handleMarketplaceBack = useCallback(() => {
        setShowMarketplace(false);
        handleOpenChange(true);
      }, [handleOpenChange]);

      if (disabled) {
        return (
          <DisabledProtocolsTrigger
            tooltipText={tooltipText}
            ariaLabel={t('floatingAskHeidiShortcuts.viewShortcuts')}
          />
        );
      }

      const triggerButton = (
        <ProtocolsTrigger
          isOpen={isOpen}
          ariaLabel={t('floatingAskHeidiShortcuts.viewShortcuts')}
        />
      );

      // When enabled, render the full menu with tooltip
      return (
        <>
          <div className="shrink-0">
            {isMobile ? (
              <ProtocolsMobileDrawer
                isOpen={isOpen}
                onOpenChange={handleOpenChange}
                triggerButton={triggerButton}
                tooltipText={tooltipText}
                pinnedChips={pinnedChips}
                onPinnedChipClick={onPinnedChipClick}
                onBrowseAllClick={handleBrowseAllClick}
                activeCuratedProtocols={activeCuratedProtocols}
                onProtocolSelect={handleProtocolSelect}
              />
            ) : (
              <ProtocolsDesktopDropdown
                isOpen={isOpen}
                onOpenChange={handleOpenChange}
                triggerButton={triggerButton}
                tooltipText={tooltipText}
                pinnedChips={pinnedChips}
                onPinnedChipClick={onPinnedChipClick}
                onBrowseAllClick={handleBrowseAllClick}
                activeCuratedProtocols={activeCuratedProtocols}
                onProtocolSelect={handleProtocolSelect}
                {...compactMenu}
                isAtShortcutLimit={isAtShortcutLimit}
                search={isGuidedMenuEnabled ? search : undefined}
                onRetainInputFocus={isGuidedMenuEnabled ? onRetainInputFocus : undefined}
              />
            )}
          </div>

          <ShortcutMarketplaceModal
            open={showMarketplace}
            onOpenChange={setShowMarketplace}
            pinnedShortcutIds={pinnedShortcutIds}
            pinnedShortcutLabels={pinnedShortcutLabels}
            onAddShortcut={handleMarketplaceAdd}
            onRemoveShortcut={onRemoveProtocol ? handleMarketplaceRemove : undefined}
            customShortcuts={customShortcuts}
            onEditShortcut={onEditProtocol}
            onBack={handleMarketplaceBack}
            isUS={isUS}
            isAtShortcutLimit={isAtShortcutLimit}
            shortcutCount={shortcutCount}
            maxShortcuts={maxShortcuts}
          />
        </>
      );
    }
  )
);

ProtocolsButton.displayName = 'ProtocolsButton';
